import { useEffect, useRef, useState } from "react";
import type { AgentAttachment, AgentDirectory, ConversationRecord } from "@opencloud/agent-protocol";
import { openCloudUIMessagePatchSchema } from "@opencloud/agent-protocol";
import { api, AgentApiError, browserRequestId, networkUnavailableMessage, subscribeApiReachability } from "./api.js";
import { createStandaloneConversation, readAgentDirectory } from "./conversations-api.js";
import { usesAppOwnerHarness } from "./harness-provider.js";
import { currentWorkRoot, latestActiveRoot } from "./presentation.js";
import { applyOpenCloudUIMessagePatch, reconcileOpenCloudUIMessageSnapshot, appendLegacyLiveDelta, legacyLiveDeltaFromDisplay } from "./ui-message-state.js";
import type { ConversationSnapshot, AgentAppView, AgentRuntimeChoice, PendingConversationMessage } from "./types.js";
import type { AgentEventStream, AgentFeedEvent } from "./use-agent-events.js";

/** A harness named by `?harness=` on the Agent URL; kept for the tab because routing drops the query. */
const EXPLICIT_HARNESS_KEY = "opencloud-agent-explicit-harness";
export function explicitHarness(): string | null {
  const requested = new URLSearchParams(window.location.search).get("harness");
  if (requested) window.sessionStorage.setItem(EXPLICIT_HARNESS_KEY, requested);
  return requested ?? window.sessionStorage.getItem(EXPLICIT_HARNESS_KEY);
}
if (typeof window !== "undefined") explicitHarness();

export type ChatSnapshot = ConversationSnapshot<AgentAppView | null> & { conversation: ConversationRecord };
const path = (id: string) => `/v1/conversations/${encodeURIComponent(id)}`;

/** One delivery and subscription lifecycle for every editable conversation. */
export function useConversation(input: {
  id: string | null;
  appId?: string | null;
  events?: AgentEventStream;
  onCreated: (id: string) => void;
  onSent?: (id: string, attachments: readonly AgentAttachment[]) => void;
}) {
  const [id, setId] = useState(input.id);
  const [snapshot, setSnapshot] = useState<ChatSnapshot | null>(null);
  const [directory, setDirectory] = useState<AgentDirectory | null>(null);
  const [pending, setPending] = useState<PendingConversationMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveDeltas, setLiveDeltas] = useState<Map<string, string>>(new Map());
  const snapshots = useRef(new Map<string, ChatSnapshot>());
  const generation = useRef(0);
  const viewVersion = useRef(0);
  const sending = useRef(false);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const creation = useRef<Parameters<typeof createStandaloneConversation>[0] | null>(null);
  const creationPromise = useRef<Promise<string> | null>(null);
  const idRef = useRef(id);
  idRef.current = id;
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const actionIntents = useRef(new Map<string, { requestId: string; sentAt: string }>());
  const eventStreamRef = useRef(input.events);
  eventStreamRef.current = input.events;
  const subscribeToEvents = input.events?.subscribe;

  const [directoryAttempt, setDirectoryAttempt] = useState(0);
  const directoryOffline = useRef(false);
  const errorRef = useRef(error);
  errorRef.current = error;
  useEffect(() => { const abort = new AbortController();
    void readAgentDirectory(abort.signal).then(setDirectory).catch(failure => {
      if (abort.signal.aborted) return;
      directoryOffline.current = failure instanceof AgentApiError && failure.code === "network_unavailable";
      setError(failure instanceof Error ? failure.message : "Could not load your account.");
    });
    return () => abort.abort();
  }, [directoryAttempt]);
  // An offline failure is obsolete once OpenCloud answers again, so it must not
  // outlive the outage; an account that failed to load offline is read again.
  // Every successful response reports reachability, so only an actual offline
  // failure may change state here.
  useEffect(() => subscribeApiReachability(reachable => {
    if (!reachable) return;
    const offline = networkUnavailableMessage();
    if (errorRef.current === offline) setError(current => current === offline ? null : current);
    if (directoryOffline.current) { directoryOffline.current = false; setDirectoryAttempt(attempt => attempt + 1); }
  }), []);

  useEffect(() => {
    if (input.id === id) return;
    generation.current++; viewVersion.current++; sending.current = false; creation.current = null;
    creationPromise.current = null; idRef.current = input.id;
    setId(input.id); setSnapshot(input.id ? snapshots.current.get(input.id) ?? null : null); setPending(null); setBusy(false);
    setStopping(null); setError(null); setStale(false);
    setDenied(false);
    setLiveDeltas(new Map()); actionIntents.current.clear();
  }, [input.id]);
  useEffect(() => () => { viewVersion.current++; }, []);

  useEffect(() => {
    const token = ++generation.current;
    if (!id) return;
    const abort = new AbortController();
    let timer: number | undefined;
    let eventTimer: number | undefined;
    let cursor = snapshots.current.get(id)?.cursor ?? 0;
    let refreshing = false;
    let refreshQueued = false;
    const owns = () => !abort.signal.aborted && generation.current === token;
    const commitSnapshot = (update: (previous: ChatSnapshot | null) => ChatSnapshot | null) => {
      setSnapshot((previous) => {
        const next = update(previous);
        if (next) snapshots.current.set(next.thread.id, next);
        return next;
      });
    };
    async function refresh() {
      if (!owns()) return;
      if (refreshing) { refreshQueued = true; return; }
      refreshing = true;
      try {
        const next = await api<ChatSnapshot>(path(id!), { signal: abort.signal });
        if (!owns()) return;
        if (next.cursor < cursor) return;
        cursor = next.cursor;
        commitSnapshot(previous => ({ ...next, ...(next.uiMessages && previous?.thread.id === next.thread.id && previous.uiMessages
          ? { uiMessages: reconcileOpenCloudUIMessageSnapshot(next.uiMessages, previous.uiMessages,
            new Set(next.messages.map(message => message.id)), new Set(next.runs.filter(run => ["completed", "failed", "cancelled"].includes(run.status)).map(run => run.id))) }
          : {}) }));
        setStale(false);
        setLiveDeltas(current => latestActiveRoot(next.runs)
          ? new Map([...current].filter(([messageId]) => !next.messages.some(message => message.id === messageId)))
          : new Map());
        setPending(current => current && next.messages.some(message => message.role === "user" &&
          (message.id === current.id || message.createdAt === current.sentAt && message.content === current.content)) ? null : current);
        setStopping(current => next.runs.some(run => run.id === current && ["completed", "failed", "cancelled"].includes(run.status)) ? null : current);
      } catch (failure) {
        if (!owns()) return;
        if (failure instanceof AgentApiError && [401, 403, 404].includes(failure.status)) {
          snapshots.current.delete(id!); abort.abort(); setSnapshot(null); setPending(null);
          setDenied(true); setLiveDeltas(new Map());
          setError(failure.message);
        } else setStale(true);
      } finally {
        refreshing = false;
        if (refreshQueued) {
          refreshQueued = false;
          if (owns()) void refresh();
        }
      }
    }
    const unsubscribe = subscribeToEvents?.((event) => {
      if (!owns()) return;
      if (event.type === "conversation.removed") {
        snapshots.current.delete(event.conversationId);
        if (event.conversationId !== id) return;
        abort.abort(); setSnapshot(null); setPending(null);
        setDenied(true); setLiveDeltas(new Map());
        setError("Access to this conversation was removed.");
        return;
      }
      if (event.conversationId !== id) return;
      applyFeedEvent(event);
    });
    function applyFeedEvent(event: AgentFeedEvent) {
      if (event.id && event.id <= cursor) return;
      if (event.id) cursor = event.id;
      if (event.type === "agent.continuity.status" || event.type === "agent.workspace.updated") {
        scheduleSnapshot();
        return;
      }
      let needsSnapshot = true;
      const patch = openCloudUIMessagePatchSchema.safeParse(event.data.uiMessage);
      if (patch.success && patch.data.operation === "append-text") needsSnapshot = false;
      if (patch.success) commitSnapshot(previous => {
        if (!previous) return previous;
        const runCreatedAt = patch.data.operation === "upsert-part"
          ? previous.runs.find(run => run.id === patch.data.metadata.runId)?.createdAt : undefined;
        return { ...previous, cursor: Math.max(previous.cursor, cursor),
          uiMessages: applyOpenCloudUIMessagePatch(previous.uiMessages ?? [], patch.data,
            runCreatedAt ? { runCreatedAt } : {}) };
      });
      const legacy = legacyLiveDeltaFromDisplay(event.data, patch.success);
      if (legacy) setLiveDeltas(current => appendLegacyLiveDelta(current, legacy.messageId, legacy.delta));
      if (needsSnapshot) scheduleSnapshot();
    }
    function scheduleSnapshot() {
      if (eventTimer !== undefined) return;
      eventTimer = window.setTimeout(() => {
        eventTimer = undefined; void refresh();
      }, 120);
    }
    refreshRef.current = refresh;
    const poll = async () => {
      await refresh();
      const streamed = eventStreamRef.current?.streamedConversationIds.has(id!) === true;
      if (owns()) timer = window.setTimeout(() => void poll(), eventStreamRef.current?.connected && streamed ? 4000
        : latestActiveRoot(snapshotRef.current?.runs ?? []) ? 500 : 1500);
    };
    void poll();
    return () => { abort.abort(); unsubscribe?.(); if (timer !== undefined) clearTimeout(timer); if (eventTimer !== undefined) clearTimeout(eventTimer); };
  }, [id, subscribeToEvents]);

  const canCreate = directory?.account.state === "personal" || directory?.account.state === "active";
  const streamStale = input.events?.stale === true;
  const canSend = !stale && (id ? snapshot?.conversation.canSend === true : canCreate);
  async function ensureConversation(title = "New chat"): Promise<string> {
    if (idRef.current) return idRef.current;
    if (!canCreate || denied) throw new Error("This conversation is read-only");
    if (creationPromise.current) return creationPromise.current;
    const token = viewVersion.current;
    // No AI account is chosen here: private chats use the person's server-side
    // defaults and app chats the app's one AI setting.
    const requestedHarness = explicitHarness();
    creation.current ??= { requestId: browserRequestId(), title: title.trim().slice(0, 120) || "New chat",
      ...(requestedHarness && usesAppOwnerHarness(requestedHarness)
        ? { harnessVersion: requestedHarness as NonNullable<Parameters<typeof createStandaloneConversation>[0]["harnessVersion"]> }
        : {}) };
    const intent = creation.current;
    const pendingCreation = (async () => {
      const created = input.appId
        ? (await api<{thread: {id: string}}>(`/v1/apps/${input.appId}/threads`, {method:"POST",
          // `platform` selects no account for the thread, so the app's setting decides.
          body: JSON.stringify({ requestId: intent.requestId, sentAt: new Date().toISOString(),
            fundingSource: "platform", title: intent.title }) })).thread
        : await createStandaloneConversation(intent).then((conversation) => {
          input.events?.upsertConversation(conversation);
          return conversation;
        });
      input.events?.refresh();
      if (viewVersion.current !== token) throw new Error("The selected chat changed");
      idRef.current = created.id; setId(created.id); input.onCreated(created.id);
      return created.id;
    })();
    creationPromise.current = pendingCreation;
    try { return await pendingCreation; }
    finally { if (creationPromise.current === pendingCreation) creationPromise.current = null; }
  }
  async function send(content: string, retry?: PendingConversationMessage, attachments: readonly AgentAttachment[] = [],
    runtime?: AgentRuntimeChoice) {
    const files = retry?.attachments ?? attachments;
    if (sending.current || !canSend || (!content.trim() && !files.length)) return;
    sending.current = true; setBusy(true); setError(null);
    const token = viewVersion.current;
    const question = snapshot?.question;
    // An answer continues the waiting root, which keeps its model and effort.
    const message = retry ?? { id: `optimistic:${browserRequestId()}`, requestId: browserRequestId(),
      sentAt: new Date().toISOString(), content: content.trim(), delivery: "sending" as const,
      ...(files.length ? {attachments: [...files]} : {}),
      ...(runtime && !question ? {runtime} : {}),
      kind: question ? "answer" as const : "message" as const, runId: question?.runId ?? null, questionId: question?.questionId ?? null };
    setPending({ ...message, delivery: "sending", failureCode: undefined });
    try {
      const target = await ensureConversation(content);
      const receipt = await api<{messageId: string}>(message.kind === "answer"
        ? `${path(target)}/runs/${message.runId}/answer` : `${path(target)}/messages`, {
        method: "POST", body: JSON.stringify({ requestId: message.requestId, sentAt: message.sentAt,
          ...(message.attachments?.length ? {attachmentIds: message.attachments.map(file => file.id)} : {}),
          content: message.content, ...(message.kind === "answer" ? {runId: message.runId, questionId: message.questionId} : message.runtime ?? {}) }) });
      input.onSent?.(target, message.attachments ?? []);
      if (viewVersion.current !== token) return;
      setPending(current => current?.requestId === message.requestId ? { ...current, id: receipt.messageId, delivery: "sent" } : current);
      await refreshRef.current();
    } catch (failure) {
      if (viewVersion.current !== token) return;
      const failureCode = failure instanceof AgentApiError ? failure.code : undefined;
      setPending(current => current?.requestId === message.requestId ? { ...current, delivery: "failed", failureCode } : current);
      setError(failure instanceof Error ? failure.message : "The message could not be sent.");
    } finally { if (viewVersion.current === token) { sending.current = false; setBusy(false); } }
  }
  async function stop() {
    // Stop ends the work in progress; a message queued behind it then starts.
    const run = currentWorkRoot(snapshot?.runs ?? []);
    const target = run ? `runs/${run.id}/cancel` : pending ? `requests/${pending.requestId}/cancel` : null;
    if (!id || !target || !snapshot?.conversation.canCancel || stopping) return;
    const token = viewVersion.current;
    setStopping(run?.id ?? pending!.requestId);
    try {
      await performAction(target);
      if (viewVersion.current === token) { setPending(null); setStopping(null); }
    } catch (failure) {
      if (viewVersion.current === token) { setStopping(null); setError(failure instanceof Error ? failure.message : "Could not stop the Agent."); }
    }
  }
  async function performAction(action: string) {
    if (!id) return;
    const token = viewVersion.current;
    let intent = actionIntents.current.get(action);
    if (!intent) { intent = { requestId: browserRequestId(), sentAt: new Date().toISOString() }; actionIntents.current.set(action, intent); }
    await api(`${path(id)}/${action}`, { method: "POST", body: JSON.stringify(intent) });
    if (viewVersion.current !== token) return;
    actionIntents.current.delete(action);
    await refreshRef.current();
  }
  async function retry(target: string, kind: "run" | "message") {
    if (!canSend || sending.current) return false;
    const token = viewVersion.current;
    sending.current = true; setBusy(true); setError(null);
    try { await performAction(kind === "run" ? `runs/${target}/retry` : `messages/${target}/regenerate`); return true; }
    catch (failure) {
      if (viewVersion.current === token) setError(failure instanceof Error ? failure.message : "Could not retry the request.");
      return false;
    } finally { if (viewVersion.current === token) { sending.current = false; setBusy(false); } }
  }
  return { id, snapshot, directory, pending, busy, stale: stale || streamStale, denied, stopping: Boolean(stopping), error, canSend,
    ensureConversation, viewKey: viewVersion.current,
    liveText: [...liveDeltas.values()].at(-1) ?? "", send, stop, retry,
    refresh: () => refreshRef.current(), clearError: () => setError(null) };
}
